import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading, Typography } from '../../common/Typography';
import { Card } from '../../common/Card';
import { Button } from '../../common';
import { format, parseISO } from 'date-fns';
import { homeSectionHeading, homeElevatedInset } from '../../home/homeDashboardStyles';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';
import { SessionTypeChip, SessionTypeIcon, sessionTypeTone } from './SessionTypeChip';

const sessionsStripClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;
const sessionsCardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start border-l-[3px]';

function getWinRateColor(winRatePct) {
  if (winRatePct == null) return 'text-text-muted';
  if (winRatePct > 50) return 'text-success';
  if (winRatePct < 50) return 'text-danger';
  return 'text-text-secondary';
}

export function RecentSessionCard({ session, currentUser, sessionWinRates, onViewSessionPerformance, compact = false }) {
  const sessionDate = session.session_date
    ? format(parseISO(session.session_date), 'EEE, d MMM')
    : 'TBD';

  const gamesCount = session.session_games?.length ?? 0;
  const didParticipate =
    currentUser?.id &&
    (session.session_signups?.some((s) => s.player_id === currentUser.id) ||
      session.session_players?.some((p) => p.player_id === currentUser.id));

  const playerStats = sessionWinRates?.get(session.id);
  const winRatePct = didParticipate ? playerStats?.winRatePct ?? null : null;
  const typeTone = sessionTypeTone(session);

  return (
    <Card
      variant="default"
      size={compact ? 'small' : 'medium'}
      className={`flex flex-col ${compact ? 'gap-2 shadow-none bg-surface-subtle/40' : `gap-3 ${homeElevatedInset}`} ${typeTone.accent} ${sessionsCardClass}`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center justify-center rounded-lg shrink-0 ${compact ? 'w-8 h-8' : 'w-9 h-9 rounded-xl'} ${typeTone.well}`}
            aria-hidden
          >
            <SessionTypeIcon session={session} />
          </span>
          <SessionTypeChip session={session} />
        </div>
        <Typography variant="caption" className="shrink-0 text-[0.65rem] font-semibold uppercase tracking-wide text-text-muted">
          Finished
        </Typography>
      </div>

      <div className="flex flex-col gap-0.5 min-w-0">
        <Typography className={`font-semibold leading-snug text-text-primary ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-2'}`}>
          {session.name || 'Session'}
        </Typography>
        {compact ? (
          <Typography variant="caption" className="text-text-secondary leading-snug line-clamp-1">
            {sessionDate}
            {gamesCount > 0 ? ` · ${gamesCount} game${gamesCount !== 1 ? 's' : ''}` : ''}
            {winRatePct != null && (
              <>
                {' · '}
                <span className={`font-semibold tabular-nums ${getWinRateColor(winRatePct)}`}>
                  {winRatePct}% win rate
                </span>
              </>
            )}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" className="text-text-secondary leading-snug">
              {sessionDate}
              {gamesCount > 0 ? ` · ${gamesCount} game${gamesCount !== 1 ? 's' : ''}` : ''}
            </Typography>
            {winRatePct != null && (
              <Typography
                variant="caption"
                className={`font-semibold tabular-nums leading-snug ${getWinRateColor(winRatePct)}`}
              >
                {winRatePct}% win rate
              </Typography>
            )}
          </>
        )}
      </div>

      {didParticipate && onViewSessionPerformance && (
        <div className="mt-auto">
          <Button
            variant={compact ? 'outline' : 'primary'}
            size="small"
            className="w-full text-xs font-semibold"
            onClick={(e) => {
              e.stopPropagation();
              onViewSessionPerformance(session.id);
            }}
          >
            View my performance
          </Button>
        </div>
      )}
    </Card>
  );
}

/**
 * Recent sessions carousel for My Club dashboard — horizontal snap scroll on mobile.
 *
 * @param {boolean} [embedded=false] - When true, omits the outer heading/View all row so the
 *   carousel can be used inside a ClubOverviewSection that already has a header.
 */
export function ClubRecentSessionsCarousel({
  sessions,
  currentUser,
  sessionWinRates,
  onViewSessionPerformance,
  clubId,
  embedded = false,
}) {
  const navigate = useNavigate();

  if (!sessions || sessions.length === 0) return null;

  const viewAllTo = clubId ? `/clubs/${clubId}?tab=club` : null;

  if (embedded) {
    return (
      <>
        <div
          role="region"
          aria-label="Recent sessions"
          className={sessionsStripClass}
          onClick={(e) => e.stopPropagation()}
        >
          {sessions.slice(0, 5).map((session) => (
            <RecentSessionCard
              key={session.id}
              session={session}
              currentUser={currentUser}
              sessionWinRates={sessionWinRates}
              onViewSessionPerformance={onViewSessionPerformance}
            />
          ))}
        </div>
        {sessions.length > 1 ? (
          <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
            Swipe for past sessions
          </Typography>
        ) : null}
      </>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3">
        <Heading level={2} className={homeSectionHeading}>Recent Sessions</Heading>
        {viewAllTo ? (
          <Button
            type="button"
            onClick={() => navigate(viewAllTo)}
            variant="text"
            className="text-sm shrink-0 font-semibold text-primary"
          >
            View all
          </Button>
        ) : null}
      </div>

      <div role="region" aria-label="Recent sessions" className={sessionsStripClass}>
        {sessions.slice(0, 5).map((session) => (
          <RecentSessionCard
            key={session.id}
            session={session}
            currentUser={currentUser}
            sessionWinRates={sessionWinRates}
            onViewSessionPerformance={onViewSessionPerformance}
          />
        ))}
      </div>

      {sessions.length > 1 ? (
        <Typography variant="caption" className="mt-3 text-center text-text-muted px-2 sm:hidden">
          Swipe sideways for more · Tap a card for session performance
        </Typography>
      ) : null}
    </div>
  );
}
